import * as React from 'react';

import { createNewTopExpression } from 'bones/utils/queryBuilder/helpers/create';
import { getUsedVariables } from 'bones/utils/queryBuilder/helpers/getUsedVariables';
import { DraftTopExpression, Variable } from 'bones/utils/queryBuilder/types';

export interface QueryBuilderContextValue {
  topExpression: DraftTopExpression;
  validVariables?: Record<string, string[]>;
  otherVariables?: Record<string, string[]>;
}

export const QueryBuilderContext =
  React.createContext<QueryBuilderContextValue>({
    topExpression: createNewTopExpression(),
  });

export const useQueryBuilderContext = () => {
  const { topExpression, validVariables, otherVariables } =
    React.useContext(QueryBuilderContext);

  const usedVariables = React.useMemo(() => {
    const result: Record<string, string[]> = {};
    const assignVariables = (variables: Record<string, string[]>) =>
      Object.entries(variables).forEach(([iri, ids]) =>
        ids.forEach((id) => {
          if (!result[iri]) {
            result[iri] = [];
          }
          if (!result[iri].includes(id)) {
            result[iri].push(id);
          }
        })
      );

    assignVariables(validVariables || {});
    assignVariables(getUsedVariables(topExpression));
    assignVariables(otherVariables || {});
    return result;
  }, [topExpression, validVariables, otherVariables]);

  const getVariableLabel = React.useCallback(
    (label: string, { id, iri }: Variable) => {
      const usedIds = usedVariables[iri];
      if (!usedIds || usedIds.length <= 1) {
        return { label, num: null };
      }

      const idx = usedIds.indexOf(id);

      return { label, num: idx + 1 };
    },
    [usedVariables]
  );

  return { topExpression, validVariables, usedVariables, getVariableLabel };
};
